import type { LocationQuery } from 'vue-router';

import { ASSIGNMENT_TYPE } from '@/types/capacityCalendar';
import type { PoActivityAction, PoActivityEvent, PoActivityQuery, PoActivitySource } from '@/types/poActivity';
import { PO_ACTIVITY_ACTION, PO_ACTIVITY_ACTIONS, PO_ACTIVITY_SOURCE, PO_ACTIVITY_SOURCES } from '@/types/poActivity';
import type { PoAssignmentRef } from '@/types/purchaseOrders';
import { toDateKey } from '@/utils/capacityCalendar';
import { isoWeekNumber } from '@/utils/isoWeek';
import { trackerSentence } from '@/utils/trackerEventSentence';

const LOCALE = 'en-US';
const MS_PER_DAY = 86_400_000;
const DAYS_PER_WEEK = 7;
const DEFAULT_RANGE = '30d';

export const ACTIVITY_RANGES = ['7d', '30d', '90d', 'all'] as const;
export type ActivityRange = (typeof ACTIVITY_RANGES)[number];

const RANGE_DAYS: Record<Exclude<ActivityRange, 'all'>, number> = { '7d': 7, '30d': 30, '90d': 90 };

export interface ActivityFilters {
  po: string | null;
  // A reservation has no PO number; the board deep-links it by the assignment id of its
  // first portion, and the API matches any event whose payload lists that id.
  assignmentId: string | null;
  vendors: string[];
  actions: PoActivityAction[];
  sources: PoActivitySource[];
  actors: string[];
  // Customer mappings' one-letter codes, upper-cased; see extractCustomerCode.
  customers: string[];
  range: ActivityRange;
}

const LIST_SEPARATOR = ',';

const CUSTOMER_CODE = /^[A-Za-z]$/;

// What an empty URL means — and what "Clear filters" returns to.
export const EMPTY_FILTERS: ActivityFilters = {
  po: null,
  assignmentId: null,
  vendors: [],
  actions: [],
  sources: [],
  actors: [],
  customers: [],
  range: DEFAULT_RANGE,
};

export function hasActiveFilters(filters: ActivityFilters): boolean {
  return (
    filters.po !== null ||
    filters.assignmentId !== null ||
    filters.range !== DEFAULT_RANGE ||
    [filters.vendors, filters.actions, filters.sources, filters.actors, filters.customers].some((list) => list.length > 0)
  );
}

const utcDate = (isoDate: string): Date => new Date(`${isoDate}T00:00:00Z`);
const shortDate = (date: Date, withYear = false): string =>
  date.toLocaleDateString(LOCALE, { month: 'short', day: 'numeric', timeZone: 'UTC', ...(withYear ? { year: 'numeric' } : {}) });

export function weekNumberLabel(weekStart: string): string {
  return `Wk ${isoWeekNumber(weekStart)}`;
}

export function weekDatesLabel(weekStart: string): string {
  const monday = utcDate(weekStart);
  const sunday = new Date(monday.getTime() + (DAYS_PER_WEEK - 1) * MS_PER_DAY);
  return `${shortDate(monday)} – ${shortDate(sunday)}`;
}

export function weekLabel(weekStart: string): string {
  return `${weekNumberLabel(weekStart)} · ${weekDatesLabel(weekStart)}`;
}

export function dayGroupLabel(iso: string, today: Date): string {
  const date = new Date(iso);
  const key = toDateKey(date);
  const todayKey = toDateKey(today);
  const yesterdayKey = toDateKey(new Date(today.getTime() - MS_PER_DAY));
  const sameYear = date.getFullYear() === today.getFullYear();
  const dayName = date.toLocaleDateString(LOCALE, { weekday: 'long', month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
  if (key === todayKey) return `Today · ${dayName}`;
  if (key === yesterdayKey) return `Yesterday · ${dayName}`;
  return dayName;
}

// Expects events in createdAt-descending order, which the API guarantees: a day group is
// closed as soon as the date changes rather than being reopened later.
export function groupByDay(events: PoActivityEvent[], today: Date): { key: string; label: string; events: PoActivityEvent[] }[] {
  return events.reduce<{ key: string; label: string; events: PoActivityEvent[] }[]>((groups, event) => {
    const key = toDateKey(new Date(event.createdAt));
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.events.push(event);
      return groups;
    }
    return [...groups, { key, label: dayGroupLabel(event.createdAt, today), events: [event] }];
  }, []);
}

export function describeEvent(event: PoActivityEvent): { verb: string; where: string } {
  const { payload } = event;
  switch (payload.action) {
    case PO_ACTIVITY_ACTION.SCHEDULED:
      return { verb: 'scheduled', where: `into ${weekLabel(payload.weekStart)}` };
    case PO_ACTIVITY_ACTION.MOVED:
      return { verb: 'moved', where: `to ${weekLabel(payload.toWeekStart)}` };
    case PO_ACTIVITY_ACTION.QUANTITY_CHANGED:
      return { verb: 'changed quantity', where: `on ${weekLabel(payload.weekStart)}` };
    case PO_ACTIVITY_ACTION.REMOVED:
      return { verb: 'removed', where: `from ${weekLabel(payload.weekStart)}` };
    case PO_ACTIVITY_ACTION.NOTE_CHANGED:
      return { verb: payload.note ? 'added a note' : 'cleared the note', where: `on ${weekLabel(payload.weekStart)}` };
    case PO_ACTIVITY_ACTION.MARKED_SCHEDULED:
      return { verb: 'marked as fully scheduled', where: '' };
    case PO_ACTIVITY_ACTION.UNMARKED_SCHEDULED:
      return { verb: 'unmarked as fully scheduled', where: '' };
    default:
      // A tracker sentence is a whole line, so it reads after the PO rather than after the actor.
      return { verb: '', where: trackerSentence(event) ?? payload.action.replaceAll('_', ' ') };
  }
}

// A PO row links to the per-PO view. A reservation cannot: it is labelled by its note (the
// only identity a manual block has), then by its PO reference, then generically.
export function subjectOf(event: PoActivityEvent, canOpenTracker: boolean): { text: string; poLink: string | null; trackerLink?: string } {
  // Tracker events have no block, so no assignment type, but always a PO; they open in the tracker.
  if (event.source === PO_ACTIVITY_SOURCE.TRACKER && event.poNumber) {
    if (!canOpenTracker) return { text: event.poNumber, poLink: null };
    const params = new URLSearchParams({ vendor: event.vendor.id, po: event.poNumber });
    return { text: event.poNumber, poLink: null, trackerLink: `/po-tracker?${params.toString()}` };
  }
  if (event.assignmentType === ASSIGNMENT_TYPE.po && event.poNumber) {
    return { text: event.poNumber, poLink: event.poNumber };
  }
  const note = 'note' in event.payload ? event.payload.note : null;
  if (note) return { text: `reservation “${note}”`, poLink: null };
  if (event.poNumber) return { text: `reservation (${event.poNumber})`, poLink: null };
  return { text: 'a reservation', poLink: null };
}

// The block the newest event touched, for the board deep link. Events arrive newest first.
// A removal's block is gone, and a schedule mark touches no block; both are skipped.
export function latestTouchedAssignmentId(events: PoActivityEvent[]): string | null {
  const event = events.find((candidate) => candidate.payload.action !== PO_ACTIVITY_ACTION.REMOVED && 'assignmentIds' in candidate.payload);
  return (event && 'assignmentIds' in event.payload ? event.payload.assignmentIds[0] : undefined) ?? null;
}

export function scheduleByWeek(
  assignments: PoAssignmentRef[],
  codeById: Map<string, string>,
): { weekStart: string; portions: { typeCode: string; quantity: number }[] }[] {
  const byWeek = new Map<string, { typeCode: string; quantity: number }[]>();
  [...assignments]
    .sort((a, b) => a.weekStart.localeCompare(b.weekStart) || a.capacityTypeId.localeCompare(b.capacityTypeId))
    .forEach((ref) => {
      const portions = byWeek.get(ref.weekStart) ?? [];
      portions.push({ typeCode: codeById.get(ref.capacityTypeId) ?? ref.capacityTypeId, quantity: ref.quantity });
      byWeek.set(ref.weekStart, portions);
    });
  return [...byWeek].map(([weekStart, portions]) => ({ weekStart, portions }));
}

const first = (value: LocationQuery[string]): string | null => (Array.isArray(value) ? (value[0] ?? null) : (value ?? null));
const isAction = (value: string | null): value is PoActivityAction => (PO_ACTIVITY_ACTIONS as readonly string[]).includes(value ?? '');
const isSource = (value: string): value is PoActivitySource => (PO_ACTIVITY_SOURCES as readonly string[]).includes(value);
const isRange = (value: string | null): value is ActivityRange => (ACTIVITY_RANGES as readonly string[]).includes(value ?? '');
// One comma-separated query value → distinct non-empty items, in order.
const list = (value: string | null): string[] => [
  ...new Set(
    (value ?? '')
      .split(LIST_SEPARATOR)
      .map((part) => part.trim())
      .filter((part) => part.length > 0),
  ),
];
const customerCodes = (values: string[]): string[] => [...new Set(values.filter((value) => CUSTOMER_CODE.test(value)).map((value) => value.toUpperCase()))];

export function filtersFromQuery(query: LocationQuery): ActivityFilters {
  const range = first(query.range);
  return {
    po: first(query.po) || null,
    assignmentId: first(query.assignmentId) || null,
    vendors: list(first(query.vendors)),
    actions: list(first(query.actions)).filter(isAction),
    sources: list(first(query.source)).filter(isSource),
    actors: list(first(query.actors)),
    customers: customerCodes(list(first(query.customers))),
    range: isRange(range) ? range : DEFAULT_RANGE,
  };
}

const joinList = (values: string[]): string | null => (values.length > 0 ? values.join(LIST_SEPARATOR) : null);
const nonEmpty = <T>(values: T[]): T[] | undefined => (values.length > 0 ? values : undefined);

export function queryFromFilters(filters: ActivityFilters): Record<string, string> {
  const entries: [string, string | null][] = [
    ['po', filters.po],
    ['assignmentId', filters.assignmentId],
    ['vendors', joinList(filters.vendors)],
    ['actions', joinList(filters.actions)],
    ['source', joinList(filters.sources)],
    ['actors', joinList(filters.actors)],
    ['customers', joinList(filters.customers)],
    ['range', filters.range === DEFAULT_RANGE ? null : filters.range],
  ];
  return Object.fromEntries(entries.filter((entry): entry is [string, string] => entry[1] !== null));
}

export function rangeStart(range: ActivityRange, today: Date): string | undefined {
  if (range === 'all') return undefined;
  return toDateKey(new Date(today.getTime() - RANGE_DAYS[range] * MS_PER_DAY));
}

export function toApiQuery(filters: ActivityFilters, today: Date): PoActivityQuery {
  return {
    poNumber: filters.po ?? undefined,
    assignmentId: filters.assignmentId ?? undefined,
    vendorIds: nonEmpty(filters.vendors),
    actions: nonEmpty(filters.actions),
    sources: nonEmpty(filters.sources),
    actorEmails: nonEmpty(filters.actors),
    customerCodes: nonEmpty(filters.customers),
    from: rangeStart(filters.range, today),
  };
}
